import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import type { ErpPropertyOption } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const NOT_MAPPED_LABEL = "Not mapped";

interface SearchableErpOptionSelectProps {
  options: ErpPropertyOption[];
  /** The selected ERP value, or empty when not mapped. */
  value: string;
  /** Called with the chosen ERP value, or empty to clear the mapping. */
  onValueChange: (value: string) => void;
}

/**
 * A searchable combobox of ERP options with a "Not mapped" entry, for option
 * lists too long to scan in a plain select.
 * @param props - SearchableErpOptionSelectProps
 * @returns The combobox element
 */
export function SearchableErpOptionSelect({
  options,
  value,
  onValueChange,
}: SearchableErpOptionSelectProps) {
  const [open, setOpen] = useState(false);
  const isMapped = value !== "";
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? value;

  const handleSelect = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-auto min-h-8 w-full justify-between whitespace-normal px-3 py-1.5 text-sm font-normal",
            !isMapped && "text-muted-foreground",
          )}
        >
          <span className="min-w-0 break-words text-left">
            {isMapped ? selectedLabel : NOT_MAPPED_LABEL}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0"
        align="end"
      >
        <Command>
          <CommandInput placeholder="Search values..." className="h-8" />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty>No value found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={NOT_MAPPED_LABEL}
                onSelect={() => handleSelect("")}
              >
                <Check
                  className={cn(
                    "mr-2 h-3 w-3 shrink-0",
                    isMapped ? "opacity-0" : "opacity-100",
                  )}
                />
                {NOT_MAPPED_LABEL}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-3 w-3 shrink-0",
                      option.value === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
